import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import {
  getLabReportByVisit,
  getLabReportById,
  finalizeLabReport,
} from "../../../api/apiService";
import { printDocumentHtml, buildLabReportHtml, resolveHospitalName } from "../../../utils/printEngine";

const ViewCreateLabRepor = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setReport(null);
        setError(null);
        const reportId = location.state?.reportId;
        const visitId = location.state?.visitId;
        let res;
        if (reportId) {
          res = await getLabReportById(reportId);
        } else if (visitId) {
          res = await getLabReportByVisit(visitId);
        }
        const data = res?.data || res;
        if (data) {
          const normalized = Array.isArray(data)
            ? { tests: data, status: data[0]?.status || "draft", _id: data[0]?._id, visitId: data[0]?.visitId || visitId }
            : data;
          setReport(normalized);
        } else {
          setError("No lab report found.");
        }
      } catch (err) {
        console.error("Error fetching lab report:", err);
        setError(err.message || "Failed to load lab report.");
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [location.state]);

  const handlePrint = () => {
    if (!report) return;
    const html = buildLabReportHtml({
      hospitalName: resolveHospitalName(user?.hospital_name),
      report,
    });
    printDocumentHtml({
      title: `Lab Report - ${report._id || "Report"}`,
      contentHtml: html,
    });
  };

  const handleFinalize = async (testType) => {
    if (!report?._id) return;
    try {
      const res = await finalizeLabReport(report._id, testType);
      const data = res?.data || res;
      if (data) setReport(data);
    } catch (err) {
      console.error("Error finalizing:", err);
    }
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="text-center p-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading lab report...</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="container-fluid">
        <div className="text-center p-5">
          <div className="alert alert-warning">
            {error || "No report data available."}
          </div>
          <button
            className="customeBtn btnLightBlueBorder"
            onClick={() => navigate("/select-patient-lab")}
          >
            Back to Patient Selection
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container-fluid">
        <div className="creat-lab-page py-2 px-2">
          <div className="allMainFeilds">
            <div className="basicDetails">
              <div className="row">
                <div className="col-xs-12 col-md-8">
                  <div className="viewBaiscDet">
                    <h4>Report Details</h4>
                    <div className="row">
                      <div className="col-xs-12 col-md-6">
                        <div className="viewReportsDet">
                          <label>Report ID</label>
                          <p>{report._id || "N/A"}</p>
                        </div>
                      </div>
                      <div className="col-xs-12 col-md-6">
                        <div className="viewReportsDet">
                          <label>Visit ID</label>
                          <p>{report.visitId || "N/A"}</p>
                        </div>
                      </div>
                      <div className="col-xs-12 col-md-6">
                        <div className="viewReportsDet">
                          <label>Patient ID</label>
                          <p>{report.patientId || "N/A"}</p>
                        </div>
                      </div>
                      <div className="col-xs-12 col-md-6">
                        <div className="viewReportsDet">
                          <label>Status</label>
                          <p>{report.status || "draft"}</p>
                        </div>
                      </div>
                    </div>

                    {/* Render each test in the tests[] array */}
                    {report.tests && report.tests.length > 0 ? (
                      report.tests.map((test, idx) => (
                        <div key={idx} className="mt-4">
                          <h4>
                            {test.displayName || test.testType}
                            {test.status && (
                              <span
                                className={`badge ms-2 ${test.status === "finalized" ? "bg-success" : "bg-secondary"}`}
                              >
                                {test.status}
                              </span>
                            )}
                          </h4>
                          <div className="row">
                            {test.sampleId && (
                              <div className="col-xs-12 col-md-6">
                                <div className="viewReportsDet">
                                  <label>Sample ID</label>
                                  <p>{test.sampleId}</p>
                                </div>
                              </div>
                            )}
                            {test.analystName && (
                              <div className="col-xs-12 col-md-6">
                                <div className="viewReportsDet">
                                  <label>Analyst</label>
                                  <p>{test.analystName}</p>
                                </div>
                              </div>
                            )}
                            {test.equipmentId && (
                              <div className="col-xs-12 col-md-6">
                                <div className="viewReportsDet">
                                  <label>Equipment ID</label>
                                  <p>{test.equipmentId}</p>
                                </div>
                              </div>
                            )}
                            {test.reportDate && (
                              <div className="col-xs-12 col-md-6">
                                <div className="viewReportsDet">
                                  <label>Report Date</label>
                                  <p>
                                    {new Date(
                                      test.reportDate,
                                    ).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                          {/* Test parameters */}
                          {test.parameters && test.parameters.length > 0 && (
                            <table className="table table-bordered mt-2">
                              <thead>
                                <tr>
                                  <th>Parameter</th>
                                  <th>Value</th>
                                  <th>Unit</th>
                                  <th>Reference Range</th>
                                </tr>
                              </thead>
                              <tbody>
                                {test.parameters.map((p, pIdx) => (
                                  <tr key={pIdx}>
                                    <td>{p.parameterName}</td>
                                    <td>{p.parameterValue}</td>
                                    <td>{p.unit || "-"}</td>
                                    <td>{p.referenceRange || "-"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                          {test.observations && (
                            <div className="viewReportsDet">
                              <label>Observations</label>
                              <p>{test.observations}</p>
                            </div>
                          )}
                          {test.status !== "finalized" && (
                            <button
                              className="customeBtn btnLightBlue btn-sm mt-2"
                              onClick={() => handleFinalize(test.testType)}
                            >
                              Finalize {test.displayName || test.testType}
                            </button>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="alert alert-info mt-3">
                        No test entries found in this report.
                      </div>
                    )}
                  </div>

                  <div className="basicDetailsAction mt-5">
                    <button
                      type="button"
                      className="customeBtn btnLightBlueBorder me-2 d-inline-flex align-items-center gap-2"
                      onClick={handlePrint}
                      title="Print Clinical Diagnostic Report"
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                        <rect x="6" y="14" width="12" height="8"></rect>
                      </svg>
                      <span>Print Lab Report</span>
                    </button>
                    <button
                      className="customeBtn btnLightBlueBorder me-2"
                      onClick={() => navigate("/select-patient-lab")}
                    >
                      Create New Report
                    </button>
                    {report.tests &&
                      report.tests.length > 0 &&
                      report.status !== "finalized" && (
                        <button
                          className="customeBtn btnLightBlue me-2"
                          onClick={() => handleFinalize()}
                        >
                          Finalize All Tests
                        </button>
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewCreateLabRepor;
